import {
  Alert,
  ConfirmModal,
  CustomContainedButton,
  CustomSelect,
  Translate,
} from '@innovatrics/checkin-components'
import {
  Box,
  Divider,
  Typography,
  InputLabel,
  Grid,
  MenuItem,
  useMediaQuery,
} from '@mui/material'
import { LocalizationProvider } from '@mui/lab'
import React, { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import enLocale from 'date-fns/locale/en-GB'
import AdapterDateFns from '@mui/lab/AdapterDateFns'

import {
  CheckinCustomer,
  useUpdateCheckinCustomerMutation,
} from '../generated/graphql'
import { Steps } from '../views'
import { AlertBox, DocumentReviewInput } from '.'

const currnetMills = new Date().getTime()
const currentTime = currnetMills % 86400000
const timezoneOffset = new Date().getTimezoneOffset() * 60000
const currentDate = new Date(currnetMills - currentTime + timezoneOffset)

const schema = yup
  .object({
    surname: yup.string().min(2).trim().required(),
    givenName: yup.string().min(2).trim().required(),
    dateOfBirth: yup.date().max(currentDate).required(),
    dateOfExpiry: yup.date().min(currentDate).required(),
    dateOfArrival: yup.date().min(currentDate).required(),
    nationality: yup.string().min(2).trim().required(),
    documentNumber: yup.string().min(2).trim().required(),
    documentType: yup.string().required(),
    address: yup.string().min(2).trim().required(),
    email: yup.string().email().min(2).trim().required(),
  })
  .required()

export interface DocumentReviewProps {
  data: CheckinCustomer
  setStep: React.Dispatch<React.SetStateAction<Steps>>
  setStaticResult: React.Dispatch<any>
}

export const DocumentReviewContainer = ({
  data,
  setStep,
  setStaticResult,
}: DocumentReviewProps) => {
  const matches = useMediaQuery('(min-width:750px)')

  const methods = useForm({
    mode: 'all',
    defaultValues: data,
    resolver: yupResolver(schema),
  })

  const {
    control,
    getValues,
    watch,
    trigger,
    formState: { errors },
  } = methods

  const [errorModalVisible, setErrorModalVisible] = useState(false)

  const [dateOfBirth, setDateOfBirth] = useState<Date>(
    new Date(getValues()?.dateOfBirth)
  )
  const [dateOfExpiry, setDateOfExpiry] = useState<Date>(
    new Date(getValues()?.dateOfExpiry)
  )
  const [dateOfArrival, setDateOfArrival] = useState<Date>(
    new Date(getValues()?.dateOfArrival)
  )
  const [showAlert, setShowAlert] = useState(true)

  useEffect(() => {
    setDateOfBirth(getValues()?.dateOfBirth)
    setDateOfExpiry(getValues()?.dateOfExpiry)
    setDateOfArrival(getValues()?.dateOfArrival)
  })

  const notEnoughInfo =
    !data?.givenName ||
    !data?.surname ||
    !data?.dateOfBirth ||
    !data?.nationality ||
    !data?.documentNumber ||
    !data?.documentType ||
    !data?.dateOfExpiry ||
    !data?.address

  const onlyOnePage = data.uploadedBackPagePath == null

  const sameFrontPage =
    data?.frontPageType != null &&
    data?.frontPageType == 'front' &&
    data?.backPageType == 'front'

  const sameBackPage =
    data?.backPageType != null &&
    data?.frontPageType == 'back' &&
    data?.backPageType == 'back'

  const noInfo =
    !data?.givenName &&
    !data?.surname &&
    !data?.dateOfBirth &&
    !data?.nationality &&
    !data?.documentNumber &&
    !data?.documentType &&
    !data?.dateOfExpiry &&
    !data?.address

  const [{ fetching }, updateDocumentFields] =
    useUpdateCheckinCustomerMutation()
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [isOpen, setOpen] = useState(false)

  useEffect(() => {
    // @ts-ignore
    trigger('givenName')
    trigger('surname')
    trigger('dateOfBirth')
    trigger('nationality')
    trigger('documentNumber')
    trigger('dateOfExpiry')
    trigger('documentType')
    trigger('address')
  }, [])

  useEffect(() => {
    watch()
    const values = getValues()
    setIsDisabled(
      !values?.givenName ||
        !values?.surname ||
        !values?.dateOfBirth ||
        !values?.nationality ||
        !values?.documentNumber ||
        !values?.documentType ||
        !values?.dateOfExpiry ||
        !values?.dateOfArrival ||
        !values?.email ||
        !values?.address ||
        !!errors?.givenName ||
        !!errors?.surname ||
        !!errors?.dateOfBirth ||
        !!errors?.nationality ||
        !!errors?.documentNumber ||
        !!errors?.documentType ||
        !!errors?.dateOfExpiry ||
        !!errors?.dateOfArrival ||
        !!errors?.email ||
        !!errors?.address
    )
  })

  const onSubmit = (customer) => {
    customer.isConfirmed = true

    const {
      checkin,
      isOcrDone,
      isScanDone,
      frontPageType,
      backPageType,
      uploadedFrontPagePath,
      uploadedBackPagePath,
      checkinNumber,
      id,
      ...fields
    } = customer
    try {
      updateDocumentFields({
        object: fields,
        id,
      }).then((res) => {
        if (res.error) {
          setErrorModalVisible(true)
          throw new Error('Hasura error: ' + res.error)
        } else {
          setStaticResult(customer)
          setTimeout(() => {
            setStep(Steps.RESULTS)
          }, 400)
        }
      })
    } catch (error) {
      console.log(error)
      setErrorModalVisible(true)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={enLocale}>
      <Box width={'100%'} paddingY={'22px'} sx={{ background: '#F2F3F4' }}>
        <Typography
          fontSize={'24px'}
          fontWeight={'400'}
          textAlign={'center'}
          color={'secondary'}
        >
          <Translate
            entityName={'customer.form.title'}
            defaultMessage={'Check-in review'}
          />
        </Typography>
      </Box>
      <Box
        sx={{ background: '#F2F3F4', borderWidth: matches ? 0 : '0 16px' }}
        border={matches ? 'none' : '16px solid #F2F3F4'}
      >
        <Box
          sx={{ background: '#FFF' }}
          padding={matches ? 0 : '16px'}
          borderRadius={'8px'}
        >
          {showAlert && (
            <AlertBox setShowAlert={setShowAlert}>
              {noInfo ? (
                <Translate
                  entityName={'customer.form.alert.noInfo'}
                  defaultMessage={
                    'We were unable recognize some information from your document, please edit information below.'
                  }
                />
              ) : sameFrontPage ? (
                <Translate
                  entityName={'customer.form.alert.sameFrontPage'}
                  defaultMessage={
                    'We were unable recognize some information from your document, please edit information below.'
                  }
                />
              ) : sameBackPage ? (
                <Translate
                  entityName={'customer.form.alert.sameBackPage'}
                  defaultMessage={
                    'We were unable recognize some information from your document, please edit information below.'
                  }
                />
              ) : onlyOnePage && notEnoughInfo ? (
                <Translate
                  entityName={'customer.form.alert.onlyOnePage'}
                  defaultMessage={
                    'We were unable recognize some information from your document, please edit information below.'
                  }
                />
              ) : notEnoughInfo ? (
                <Translate
                  entityName={'customer.form.alert.notEnoughInfo'}
                  defaultMessage={
                    'We were unable recognize some information from your document, please edit information below.'
                  }
                />
              ) : (
                <Translate
                  entityName={'customer.form.alert.default'}
                  defaultMessage={
                    'We were unable recognize some information from your document, please edit information below.'
                  }
                />
              )}
            </AlertBox>
          )}
          <FormProvider {...methods}>
            <Grid
              container
              maxWidth={'570px'}
              alignItems={'center'}
              marginX={'auto'}
              marginBottom={matches ? '30px' : '40px'}
            >
              <Typography
                fontSize={'16px'}
                fontWeight={600}
                color={'secondary'}
                textTransform={'uppercase'}
                marginTop={'20px'}
              >
                <Translate
                  entityName={'customer.form.subtitle.general'}
                  defaultMessage={'General'}
                />
              </Typography>
              <form>
                <Grid
                  container
                  columnSpacing={{ xs: '32px' }}
                  columns={{ xs: 6 }}
                  marginTop={2}
                  marginBottom={'20px'}
                >
                  <DocumentReviewInput
                    name="dateOfArrival"
                    date={dateOfArrival}
                    setDate={setDateOfArrival}
                    minDate={currentDate}
                    errorMessage
                  />
                  <DocumentReviewInput name="email" errorMessage />
                </Grid>

                <Divider />
                <Typography
                  fontSize={'16px'}
                  fontWeight={600}
                  color={'secondary'}
                  textTransform={'uppercase'}
                  marginTop={'20px'}
                >
                  <Translate
                    entityName={'customer.form.subtitle.personal'}
                    defaultMessage={'Personal and document detail'}
                  />
                </Typography>
                <Grid
                  container
                  rowSpacing={2}
                  columnSpacing={{ xs: '32px' }}
                  columns={{ xs: 6 }}
                  marginTop={'0'}
                >
                  <DocumentReviewInput name="surname" />
                  <DocumentReviewInput name="givenName" />
                  <DocumentReviewInput
                    name="dateOfBirth"
                    date={dateOfBirth}
                    setDate={setDateOfBirth}
                    maxDate={currentDate}
                  />
                  <DocumentReviewInput name="address" multiline />
                  <DocumentReviewInput name="nationality" />
                  <Grid item xs={6}>
                    <InputLabel>
                      <Grid
                        container
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: errors?.documentType
                            ? 'error.main'
                            : 'text.disabled',
                        }}
                      >
                        <Grid item>
                          <Translate
                            entityName={`forms.documentType.tooltip`}
                            defaultMessage={'Document type'}
                          />
                        </Grid>
                        <Grid
                          item
                          container
                          alignItems={'center'}
                          width="unset"
                        >
                          {errors?.documentType && (
                            <>
                              <Translate
                                entityName={`forms.error`}
                                defaultMessage={'Warning'}
                              />
                              <Alert />
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </InputLabel>
                    <Controller
                      control={control}
                      name={'documentType'}
                      render={({ field }) => (
                        <CustomSelect
                          MenuProps={{
                            sx: {
                              '& .MuiPaper-root': {
                                marginTop: '5px',
                                borderRadius: '8px',
                              },
                              '& .MuiList-root': {
                                padding: 0,
                                background: '#fff',
                              },
                              '& .MuiDivider-root': {
                                margin: '0!important',
                                width: '100%',
                              },
                              '& .MuiMenuItem-root': {
                                padding: '12px 16px',
                                fontSize: '14px',
                                fontWeight: 500,
                                color: 'secondary',
                              },
                            },
                          }}
                          value={
                            field.value == 'identity-card' ||
                            field.value == 'passport' ||
                            field.value == '' ||
                            field.value == null
                              ? field.value
                              : 'other'
                          }
                          onOpen={() => setOpen(true)}
                          onClose={() => setOpen(false)}
                          onChange={(val) => field.onChange(val)}
                          sx={{
                            backgroundColor: '#F2F3F4',
                            borderRadius: '8px',
                            border: errors?.documentType
                              ? '1px solid #E5352B'
                              : '1px solid  rgba(255, 255, 255, 0)',
                            width: '100%',
                            marginY: '5px',
                            zIndex: isOpen ? 1500 : 1,
                          }}
                          displayEmpty
                          renderValue={(value) => {
                            switch (value) {
                              case 'identity-card':
                                return (
                                  <Translate
                                    entityName={`forms.documentType.identity-card`}
                                    defaultMessage={'ID Card'}
                                  />
                                )
                              case 'passport':
                                return (
                                  <Translate
                                    entityName={`forms.documentType.passport`}
                                    defaultMessage={'Passport'}
                                  />
                                )
                              case '':
                              case null:
                                return (
                                  <span
                                    style={{
                                      color: '#808DA0',
                                      fontWeight: 400,
                                    }}
                                  >
                                    <Translate
                                      entityName={`forms.documentType.placeholder`}
                                      defaultMessage={'Select document type'}
                                    />
                                  </span>
                                )
                              default:
                                return (
                                  <Translate
                                    entityName={`forms.documentType.other`}
                                    defaultMessage={'Other'}
                                  />
                                )
                            }
                          }}
                        >
                          <MenuItem value={'identity-card'}>
                            <Translate
                              entityName={`forms.documentType.identity-card`}
                              defaultMessage={'ID Card'}
                            />
                          </MenuItem>
                          <Divider sx={{ width: '96%', marginX: 'auto' }} />
                          <MenuItem value={'passport'}>
                            <Translate
                              entityName={`forms.documentType.passport`}
                              defaultMessage={'Passport'}
                            />
                          </MenuItem>
                          <Divider sx={{ width: '96%', marginX: 'auto' }} />
                          <MenuItem value={'other'}>
                            <Translate
                              entityName={`forms.documentType.other`}
                              defaultMessage={'Other'}
                            />
                          </MenuItem>
                        </CustomSelect>
                      )}
                    />
                  </Grid>
                  <DocumentReviewInput name="documentNumber" />
                  <DocumentReviewInput
                    name="dateOfExpiry"
                    minDate={currentDate}
                    date={dateOfExpiry}
                    setDate={setDateOfExpiry}
                  />
                </Grid>
                <Box
                  marginTop={'40px'}
                  sx={{
                    display: 'flex',
                    bottom: '0',
                    left: '0',
                    width: '100%',
                  }}
                  justifyContent="center"
                >
                  <CustomContainedButton
                    variant="contained"
                    color="primary"
                    onClick={methods.handleSubmit(onSubmit)}
                    onFocus={methods.handleSubmit(onSubmit)}
                    loading={fetching}
                    disabled={isDisabled}
                  >
                    <Translate
                      entityName={'customer.form.button.confirm'}
                      defaultMessage={'Confirm check-in'}
                    />
                  </CustomContainedButton>
                </Box>
              </form>
            </Grid>
          </FormProvider>
        </Box>
      </Box>
      <ConfirmModal
        name={'customer.error'}
        modalOpen={errorModalVisible}
        setModalOpen={setErrorModalVisible}
        handleClick={() => setErrorModalVisible(false)}
      />
    </LocalizationProvider>
  )
}
